import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { Loader2, Landmark, Search, UserPlus, X, ShieldCheck, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { searchUsersByNickname, saveOrganizacionSettings } from '@/lib/actions';
import { Organizacion } from '@/lib/types';

type FoundUser = { id: string; nickname: string; profileImageUrl: string | null };

export default function AdminArzobispadoPage() {
  const { userData, loading, jartinetData, refetch } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const arzobispado = useMemo<Organizacion | undefined>(
    () => (jartinetData?.organizaciones || []).find((o) => o.nombreCompleto === 'Arzobispado'),
    [jartinetData],
  );

  const [gestores, setGestores] = useState<string[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [savingUid, setSavingUid] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet', { replace: true });
    }
  }, [userData, loading, navigate]);

  // Sincroniza la lista local con la del documento.
  useEffect(() => {
    setGestores(arzobispado?.gestores || []);
  }, [arzobispado]);

  // Resuelve los UIDs de los gestores actuales a nombres legibles.
  useEffect(() => {
    if (gestores.length === 0) { setNames({}); return; }
    const missing = gestores.filter((uid) => !names[uid]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const fn = httpsCallable<{ uids: string[] }, { names: Record<string, string> }>(functions, 'getPublicUserNames');
        const res = await fn({ uids: gestores });
        setNames((prev) => ({ ...prev, ...(res.data?.names || {}) }));
      } catch (e) {
        console.error('Error resolviendo nombres de gestores:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gestores]);

  // Búsqueda con debounce.
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { users } = await searchUsersByNickname(term);
      setResults(users || []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const persist = async (next: string[], successMsg: string, uidBeingChanged: string) => {
    if (!arzobispado) return;
    setSavingUid(uidBeingChanged);
    const prev = gestores;
    setGestores(next); // optimista
    const result = await saveOrganizacionSettings(arzobispado.id, { gestores: next });
    if (result.success) {
      toast({ title: 'Éxito', description: successMsg });
      refetch();
    } else {
      setGestores(prev); // revertir
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setSavingUid(null);
  };

  const handleAdd = (u: FoundUser) => {
    if (gestores.includes(u.id)) {
      toast({ title: 'Ya tiene acceso', description: `${u.nickname} ya gestiona el Arzobispado.` });
      return;
    }
    setNames((prev) => ({ ...prev, [u.id]: u.nickname }));
    persist([...gestores, u.id], `${u.nickname} ahora tiene acceso al Arzobispado.`, u.id);
    setSearchTerm('');
    setResults([]);
  };

  const handleRemove = (uid: string) => {
    persist(gestores.filter((g) => g !== uid), 'Acceso revocado.', uid);
  };

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  if (!arzobispado) {
    return (
      <div className="flex h-[calc(100vh-10rem)] flex-col items-center justify-center text-center">
        <Landmark className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <p className="text-muted-foreground max-w-md">
          No se encontró la organización <strong>Arzobispado</strong>. Créala primero desde la gestión de organizaciones.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-violet-500/15 via-card/40 to-card/20 p-8 md:p-10"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-violet-500/40 blur-xl" />
            <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-violet-400/30 bg-violet-500/15 text-violet-300">
              <Landmark className="h-9 w-9" />
            </div>
          </div>
          <div>
            <h1 className="font-headline text-4xl font-bold text-foreground md:text-5xl">Acceso al Arzobispado</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Elige qué usuarios pueden gestionar el panel del Arzobispado: publicar en redes, emitir contratos y enviar cartas a toda la comunidad.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Añadir usuario */}
      <section className="rounded-2xl border border-white/8 bg-card/40 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl border border-violet-400/30 bg-violet-500/12 text-violet-300">
            <UserPlus className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-headline text-xl font-bold">Conceder acceso</h2>
            <p className="text-xs text-muted-foreground">Busca un usuario por su nombre para darle acceso.</p>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Escribe al menos 2 letras..."
            className="pl-9"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        <AnimatePresence>
          {results.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 space-y-1.5 overflow-hidden"
            >
              {results.map((u) => {
                const already = gestores.includes(u.id);
                return (
                  <div key={u.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-muted/30 p-2.5">
                    <Avatar className="h-9 w-9 border border-white/10">
                      <AvatarImage src={u.profileImageUrl || undefined} />
                      <AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate text-sm font-medium">{u.nickname}</span>
                    <Button
                      size="sm"
                      variant={already ? 'ghost' : 'default'}
                      disabled={already || savingUid === u.id}
                      onClick={() => handleAdd(u)}
                    >
                      {savingUid === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : already ? 'Ya tiene acceso' : 'Conceder'}
                    </Button>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
        {searchTerm.trim().length >= 2 && !searching && results.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground text-center py-2">No se encontraron usuarios.</p>
        )}
      </section>

      {/* Gestores actuales */}
      <section className="rounded-2xl border border-white/8 bg-card/40 p-5 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl border border-primary/30 bg-primary/12 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-headline text-xl font-bold">Con acceso actualmente</h2>
            <p className="text-xs text-muted-foreground">{gestores.length} {gestores.length === 1 ? 'usuario' : 'usuarios'} con acceso al panel.</p>
          </div>
        </div>

        {gestores.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
            <Users className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">Nadie tiene acceso todavía.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <AnimatePresence initial={false}>
              {gestores.map((uid) => (
                <motion.div
                  key={uid}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  className="flex items-center gap-3 rounded-xl border border-white/8 bg-muted/30 p-3"
                >
                  <Avatar className="h-9 w-9 border border-white/10">
                    <AvatarFallback>{(names[uid] || '?').charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate text-sm font-medium">{names[uid] || uid}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:bg-destructive/10"
                    disabled={savingUid === uid}
                    onClick={() => handleRemove(uid)}
                  >
                    {savingUid === uid ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  </Button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>
    </div>
  );
}
